import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoKeyOutline } from 'react-icons/io5';

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', permission_ids: [] });

  const load = () => {
    setLoading(true);
    Promise.all([api('/api/admin/roles'), api('/api/admin/permissions')])
      .then(([rolesData, permsData]) => {
        setRoles(rolesData);
        setPermissions(permsData || []);
        setError('');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({ name: '', slug: '', description: '', permission_ids: [] });
    setOpen(true);
  };

  const handleOpenEdit = (role) => {
    setEditing(role);
    setForm({
      name: role.name || '',
      slug: role.slug || '',
      description: role.description || '',
      permission_ids: role.permission_ids || [],
    });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || form.name.trim().toLowerCase().replace(/\s+/g, '-'),
        description: form.description.trim() || null,
        permission_ids: form.permission_ids,
      };
      if (editing) {
        await api(`/api/admin/roles/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/api/admin/roles', { method: 'POST', body: JSON.stringify(payload) });
      }
      handleClose();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role? Staff using it must be reassigned first.')) return;
    try {
      await api(`/api/admin/roles/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const togglePermission = (permId) => {
    setForm((f) => ({
      ...f,
      permission_ids: f.permission_ids.includes(permId)
        ? f.permission_ids.filter((p) => p !== permId)
        : [...f.permission_ids, permId],
    }));
  };

  if (loading) return <AdminLoading />;

  return (
    <>
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardHeader
          title="Roles"
          action={
            <Button variant="contained" onClick={handleOpenAdd} sx={{ textTransform: 'none' }}>
              Add role
            </Button>
          }
        />
        <CardContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          {roles.length === 0 ? (
            <AdminEmptyState message="No roles. Add one to get started." icon={IoKeyOutline} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell><strong>{r.name}</strong></TableCell>
                      <TableCell>{r.slug}</TableCell>
                      <TableCell>{(r.permission_ids && r.permission_ids.length) || 0}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleOpenEdit(r)}>Edit</Button>
                        {r.slug !== 'super_admin' && (
                          <Button size="small" color="error" onClick={() => handleDelete(r.id)}>Delete</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit role' : 'Add role'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="e.g. manager"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', maxHeight: 200 }}>
              {permissions.map((p) => (
                <FormControlLabel
                  key={p.id}
                  control={
                    <Checkbox
                      checked={form.permission_ids.includes(p.id)}
                      onChange={() => togglePermission(p.id)}
                    />
                  }
                  label={`${p.name} (${p.code})`}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">{editing ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
